import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plus, X } from "lucide-react";
import { useApp } from "@/lib/store";
import { productUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

export function Reveal({ children, className = "", delay = 0, as: Tag = "div", ...rest }) {
  const ref = useRef(null);
  const [inView, setIn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) { setIn(true); return undefined; }
    const ob = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setIn(true); ob.disconnect(); } }, { threshold: 0.12 });
    ob.observe(el);
    return () => ob.disconnect();
  }, []);
  return <Tag ref={ref} className={`reveal ${inView ? "in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }} {...rest}>{children}</Tag>;
}

export function CountUp({ to, suffix = "", testid }) {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  useEffect(() => {
    const el = ref.current;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!el || reduce || !("IntersectionObserver" in window)) { setVal(to); return undefined; }
    let raf;
    const ob = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      ob.disconnect();
      const t0 = performance.now();
      const step = (t) => { const k = Math.min(1, (t - t0) / 1600); setVal(Math.round(to * (1 - Math.pow(1 - k, 3)))); if (k < 1) raf = requestAnimationFrame(step); };
      raf = requestAnimationFrame(step);
    });
    ob.observe(el);
    return () => { ob.disconnect(); cancelAnimationFrame(raf); };
  }, [to]);
  return <span ref={ref} data-testid={testid} className="tabular">{val.toLocaleString()}{suffix}</span>;
}

export function SectionHead({ num, eyebrow, title, sub, action, className = "" }) {
  return (
    <Reveal className={`flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 ${className}`}>
      <div className="max-w-2xl">
        <div className="flex items-center gap-4 mb-4">{num && <span className="sec-num">{num}</span>}<span className="eyebrow">{eyebrow}</span></div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl">{title}</h2>
        {sub && <p className="mt-4 text-[var(--graphite)] text-base md:text-lg">{sub}</p>}
      </div>
      {action}
    </Reveal>
  );
}

export function PageHero({ eyebrow, title, sub, image, children, testid = "page-hero", sepia }) {
  return (
    <section data-testid={testid} className="border-b border-[var(--stone)]">
      <div className="wrap grid lg:grid-cols-12 gap-10 py-16 lg:py-24 items-center">
        <Reveal className={image ? "lg:col-span-6" : "lg:col-span-9"}>
          <p className="eyebrow mb-5">{eyebrow}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl">{title}</h1>
          {sub && <p className="mt-6 text-[var(--graphite)] text-base md:text-lg max-w-xl">{sub}</p>}
          {children}
        </Reveal>
        {image && (
          <Reveal delay={150} className="lg:col-span-6">
            <div className="img-frame aspect-[4/3] border border-[var(--stone)]"><img src={image} alt="" className={`w-full h-full object-cover ${sepia ? "sepia" : ""}`} /></div>
          </Reveal>
        )}
      </div>
    </section>
  );
}

export function FinishChips({ finishes = [] }) {
  const short = { "Hot-Dip Galvanised": "HDG", "Zinc Plated": "Zinc" };
  return <div className="flex flex-wrap gap-1.5">{finishes.map((f) => <span key={f} className="chip">{short[f] || f}</span>)}</div>;
}

export function ProductCard({ p, compact }) {
  const { add } = useApp();
  return (
    <div data-testid={`product-card-${p.slug}`} className="group card flex flex-col hover:border-[var(--brass)] transition-colors duration-300">
      <Link to={productUrl(p)} className="img-frame prod-bg aspect-square flex items-center justify-center p-6 border-b border-[var(--stone)]">
        <img src={p.images?.[0]} alt={p.name} loading="lazy" className="max-h-full max-w-full object-contain" />
      </Link>
      <div className="p-5 flex flex-col gap-3 flex-1">
        <Link to={productUrl(p)}><h3 className="text-xl leading-snug">{p.name}</h3></Link>
        <FinishChips finishes={p.finishes} />
        {!compact && (
          <div className="mt-auto pt-3 flex items-center justify-between gap-2">
            <Link to={productUrl(p)} data-testid={`view-details-${p.slug}`} className="text-sm link">View details</Link>
            <button data-testid={`add-enquiry-${p.slug}`} onClick={() => add(p)} className="text-sm inline-flex items-center gap-1 text-[var(--graphite)] hover:text-[var(--brass)] transition-colors"><Plus size={14} /> Add to enquiry</button>
          </div>
        )}
        {compact && <Link to={`/enquiry?product=${p.slug}`} className="mt-auto text-sm link inline-flex items-center gap-1">Enquire <ArrowRight size={14} /></Link>}
      </div>
    </div>
  );
}

export function Lightbox({ src, alt = "", onClose }) {
  return (
    <Dialog open={!!src} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="lightbox" className="max-w-4xl bg-[var(--paper)] p-4">
        <DialogTitle className="sr-only">{alt || "Image preview"}</DialogTitle>
        {src && <img src={src} alt={alt} className="w-full max-h-[78vh] object-contain" />}
      </DialogContent>
    </Dialog>
  );
}

export function UnitToggle() {
  const { sys, setSys } = useApp();
  return (
    <div className="inline-flex border border-[var(--stone)] rounded-sm text-xs" role="group" aria-label="Units">
      {[["imperial", "inch · lbs"], ["metric", "mm · kg"]].map(([k, l]) => (
        <button key={k} data-testid={`units-${k}`} onClick={() => setSys(k)} className={`px-3 py-1.5 transition-colors ${sys === k ? "bg-[var(--ink)] text-[var(--paper)]" : "text-[var(--graphite)] hover:text-[var(--ink)]"}`}>{l}</button>
      ))}
    </div>
  );
}

export function CookieBanner() {
  const [show, setShow] = useState(() => { try { return !localStorage.getItem("tij_cookie"); } catch { return false; } });
  if (!show) return null;
  const done = (v) => { try { localStorage.setItem("tij_cookie", v); } catch { /* ignore */ } setShow(false); };
  return (
    <div data-testid="cookie-banner" className="fixed bottom-4 left-4 right-4 md:right-auto md:max-w-md z-50 card p-5 shadow-sm">
      <p className="text-sm text-[var(--graphite)]">We use essential cookies and anonymous analytics to improve this site. <Link to="/privacy" className="link">Privacy Policy</Link></p>
      <div className="mt-4 flex gap-3">
        <button data-testid="cookie-accept" className="btn btn-brass btn-sm" onClick={() => done("all")}>Accept</button>
        <button data-testid="cookie-essential" className="btn btn-outline btn-sm" onClick={() => done("essential")}>Essential only</button>
        <button aria-label="Close" className="ml-auto text-[var(--graphite)]" onClick={() => done("essential")}><X size={16} /></button>
      </div>
    </div>
  );
}

export function Field({ label, required, error, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="label">{label}{required && <span className="text-[var(--brass)]"> *</span>}</span>
      {children}
      {error && <p className="err">{error}</p>}
    </label>
  );
}
